import { useState } from "react";
import { usePreviousTransactions } from "./usePreviousTransactions";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { handleApiError } from "Helper/index.js";
import LoadingScreen from "components/common/Loading";
import { Loader } from "@progress/kendo-react-indicators";
import { exportData } from "components/Report/exportUtility";
import { ExportConfigs } from "components/Report/exportConfigs";
import {
  DateFormatCell,
  AmountFormatCell,
  CreditAmountFormatCell,
  DebitAmountFormatCell,
} from "components/common/formatter";
import { GRID_PAGE_SIZES } from "components/common/GridComponents";

const PreviousTransactionsDialog = ({
  isOpen,
  onClose,
  entity,
  entityType = "supplier",
  exportIcon,
}) => {
  const { dataState, setDataState, loading, transactions } =
    usePreviousTransactions(isOpen ? entity : null, entityType);

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!entity) return;

    setIsExporting(true);
    try {
      let config, requestBodyArgs, fileName;
      if (entityType === "supplier") {
        config = ExportConfigs.SUPPLIER_LEDGER_PREVIOUS;
        requestBodyArgs = [entity.supplierRef];
        fileName = entity.supplierRef || "SupplierLedgerPrevious";
      }
      await exportData({
        ...config,
        requestBody: config.getRequestBody(...requestBodyArgs),
        fileName,
        onStart: () => setIsExporting(true),
        onComplete: () => setIsExporting(false),
        onError: () => setIsExporting(false),
      });
    } catch (error) {
      handleApiError(error);
      setIsExporting(false);
    }
  };

  const RefCell = (e) => (
    <td>
      <u>{e.dataItem.reference}</u>
    </td>
  );

  const renderGrid = () => (
    <Grid
      data={transactions.data}
      pageable={{
        buttonCount: 5,
        pageSizes: GRID_PAGE_SIZES,
        previousNext: true,
      }}
      skip={dataState.skip}
      take={dataState.take}
      total={transactions.total}
      onDataStateChange={(e) => setDataState(e.dataState)}
      style={{
        height: "645px",
      }}
    >
      {exportIcon && (
        <GridToolbar>
          <button
            className="btn p-0 d-flex align-items-center justify-content-center border-0"
            onClick={handleExport}
            disabled={!transactions.data.length || isExporting}
            title="Export to Excel"
          >
            <div className="dark-background">
              {isExporting ? (
                <Loader
                  size="small"
                  type="infinite-spinner"
                  className="text-dark"
                />
              ) : (
                <img
                  src={exportIcon}
                  width={18}
                  height={18}
                  alt="Export to Excel"
                />
              )}
            </div>
          </button>
        </GridToolbar>
      )}
      <GridColumn
        field="date"
        title="Date"
        width="120px"
        cell={DateFormatCell}
      />
      <GridColumn field="postingType" title="Type" width="80px" />
      <GridColumn field="reference" title="Ref" cell={RefCell} width="100px" />
      <GridColumn field="details" title="Details" />
      <GridColumn field="transactionPeriod" title="Period" width="80px" />
      <GridColumn
        field="vatAmount"
        title="VAT"
        cell={AmountFormatCell}
        width="100px"
        headerClassName="grid-header-center"
      />
      <GridColumn
        field="grossAmount"
        title="Credit"
        className="creditCol"
        cell={CreditAmountFormatCell}
        width="120px"
        headerClassName="grid-header-center"
      />
      <GridColumn
        field="grossAmount"
        title="Debit"
        className="debitCol"
        cell={DebitAmountFormatCell}
        width="120px"
        headerClassName="grid-header-center"
      />
      <GridColumn
        field="balance"
        title="Balance"
        className="balanceCol balanceTd"
        cell={AmountFormatCell}
        width="140px"
        headerClassName="grid-header-center"
      />
    </Grid>
  );

  const getTitle = () => {
    const entityName =
      entityType === "nominal"
        ? `Nominal [${entity?.nominalRef} - ${entity?.nominalName}]`
        : `Supplier [${entity?.supplierRef} - ${entity?.supplierName}]`;

    return `${entityName} Previous Transactions`;
  };

  return (
    <Dialog title={getTitle()} onClose={onClose} className="dialog-xxl">
      <div className="m-4">
        {loading && <LoadingScreen />}
        {renderGrid()}
      </div>
      <DialogActionsBar>
        <div className="dialog-footer d-flex justify-content-end">
          <button className="common-btn me-5" onClick={onClose}>
            Close
          </button>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

export default PreviousTransactionsDialog;
